import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { OrganizationSwitcher } from '@clerk/clerk-react'
import { ChevronRight, Megaphone } from 'lucide-react'

import { useShell } from '@/components/shell/shell-context'
import { useAssistantName } from '@/lib/use-assistant-name'
import { useIsPlatformAdmin } from '@/lib/auth'

const MORE_LINKS: { to: string; titleKey: string; subKey: string }[] = [
  { to: '/estimates', titleKey: 'more.estimates', subKey: 'more.estimates_sub' },
  { to: '/calls', titleKey: 'more.calls', subKey: 'more.calls_sub' },
  { to: '/dealers', titleKey: 'more.dealers', subKey: 'more.dealers_sub' },
  { to: '/suppliers', titleKey: 'more.suppliers', subKey: 'more.suppliers_sub' },
  { to: '/messaging', titleKey: 'more.messaging', subKey: 'more.messaging_sub' },
  { to: '/threads', titleKey: 'more.threads', subKey: 'more.threads_sub' },
  { to: '/activity', titleKey: 'more.activity', subKey: 'more.activity_sub' },
  { to: '/settings', titleKey: 'more.settings', subKey: 'more.settings_sub' },
]

/**
 * The fifth tab (DESIGN.md §5): every destination that isn't Today /
 * Schedule / Customers / Vehicles, as §4.8 list rows, plus the What's
 * New trigger and the OrganizationSwitcher.
 */
export default function More() {
  const { t } = useTranslation('shell')
  const assistantName = useAssistantName()
  const { openWhatsNew } = useShell()
  const isPlatformAdmin = useIsPlatformAdmin()

  // Non-platform-admins never create orgs themselves — onboarding flows
  // through curated invitations (same soft gate AuthedLayout applies).
  const orgSwitcherAppearance = isPlatformAdmin
    ? undefined
    : {
        elements: {
          organizationSwitcherPopoverActionButton__createOrganization: {
            display: 'none',
          },
        },
      }

  return (
    <div className="mx-auto max-w-2xl">
      <h1 className="text-[22px] font-bold tracking-[-0.015em] text-foreground [text-wrap:balance]">
        {t('more.title')}
      </h1>

      <div className="mt-4 overflow-hidden rounded-[14px] border border-border bg-background">
        {MORE_LINKS.map(({ to, titleKey, subKey }) => (
          <Link
            key={to}
            to={to}
            className="flex min-h-[60px] w-full items-center gap-3 border-t border-border bg-background px-3.5 py-3 transition-[background-color,transform] first:border-t-0 active:scale-[0.99] active:bg-muted [@media(hover:hover)_and_(pointer:fine)]:hover:bg-muted"
          >
            <span className="min-w-0 flex-1">
              <span className="block text-[15px] font-semibold text-foreground">
                {t(titleKey)}
              </span>
              <span className="block text-[13px] text-muted-foreground">
                {t(subKey, { assistant: assistantName })}
              </span>
            </span>
            <ChevronRight aria-hidden="true" className="size-4 shrink-0 text-ink-3" />
          </Link>
        ))}

        <button
          type="button"
          onClick={openWhatsNew}
          className="flex min-h-[60px] w-full items-center gap-3 border-t border-border bg-background px-3.5 py-3 text-left transition-[background-color,transform] active:scale-[0.99] active:bg-muted [@media(hover:hover)_and_(pointer:fine)]:hover:bg-muted"
        >
          <span className="min-w-0 flex-1">
            <span className="block text-[15px] font-semibold text-foreground">
              {t('more.whats_new')}
            </span>
            <span className="block text-[13px] text-muted-foreground">
              {t('more.whats_new_sub')}
            </span>
          </span>
          <Megaphone aria-hidden="true" className="size-4 shrink-0 text-ink-3" />
        </button>
      </div>

      <div className="mt-6 rounded-[14px] border border-border bg-background p-3.5">
        <div className="mb-2 text-[13px] font-semibold text-muted-foreground">
          {t('more.organization')}
        </div>
        <OrganizationSwitcher hidePersonal appearance={orgSwitcherAppearance} />
      </div>
    </div>
  )
}
